import { Select, TextField } from '../forms';
import { useState } from 'react';
import { Banner, BlockStack, Button, InlineStack } from '@shopify/polaris';
import { useTransferRead, type TransferClient } from './useTransferClient';
export interface Choice {
  id: number;
  label: string;
}
export function ResourceChoice({
  required,
  client,
  label,
  warehouse,
  value,
  onChange,
  disabled = false,
  binKind,
}: {
  required?: boolean;
  client: TransferClient;
  label: string;
  warehouse?: number;
  value: Choice | null;
  onChange: (value: Choice | null) => void;
  disabled?: boolean;
  binKind?: 'stock' | 'holding';
}) {
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState<number | null>(null);
  const [refresh, setRefresh] = useState(0);
  const kind = warehouse === undefined ? 'warehouses' : 'locations';
  const { data, error } = useTransferRead<{
    warehouses?: { id: number; name: string }[];
    locations?: { id: number; code: string }[];
    nextCursor: number | null;
  }>(
    client,
    `/${kind}?q=${encodeURIComponent(query)}${warehouse === undefined ? '' : '&warehouse_id=' + warehouse}${cursor ? '&cursor=' + cursor : ''}${binKind ? '&kind=' + binKind : ''}`,
    refresh,
  );
  const choices: Choice[] =
    kind === 'warehouses'
      ? (data?.warehouses || []).map((x) => ({ id: x.id, label: x.name }))
      : (data?.locations || []).map((x) => ({ id: x.id, label: x.code }));
  if (value && !choices.some((x) => x.id === value.id)) choices.unshift(value);
  return (
    <BlockStack gap="200">
      <TextField
        label={`Search ${label.toLowerCase()}`}
        value={query}
        onChange={(value) => {
          setQuery(value);
          setCursor(null);
        }}
        maxLength={100}
        disabled={disabled}
        autoComplete="off"
      />
      <Select
        label={label}
        requiredIndicator={required ?? warehouse === undefined}
        value={String(value?.id || '')}
        disabled={disabled}
        onChange={(id) => onChange(choices.find((x) => x.id === Number(id)) || null)}
        options={[
          { label: data ? 'Choose…' : 'Loading…', value: '' },
          ...choices.map((x) => ({ label: x.label, value: String(x.id) })),
        ]}
      />
      {error && (
        <Banner tone="critical">
          {error}
          <Button onClick={() => setRefresh((x) => x + 1)}>Retry choices</Button>
        </Banner>
      )}
      <InlineStack gap="200">
        <Button size="slim" disabled={disabled || !cursor} onClick={() => setCursor(null)}>
          First choices
        </Button>
        <Button
          size="slim"
          disabled={disabled || !data?.nextCursor}
          onClick={() => setCursor(data?.nextCursor || null)}
        >
          More choices
        </Button>
      </InlineStack>
    </BlockStack>
  );
}
